/**
 * The planning assistant.
 *
 * Locally `/ai` is proxied to the Python service by the dev server exactly as
 * `/api` is, so there is a single port to reach and no CORS — see the proxy
 * note in `vite.config.ts`. Deployed, the app and the assistant are at
 * different addresses, so `TP_WEB_AI_URL` puts one in front of the path; the
 * assistant allows the app's origin through its own CORS settings.
 */

import type { PlanRequest, RoutePlan } from './types';
import { clientKey } from './client';
import { aiBaseUrl } from './origin';

export interface AssistantHealth {
  ok: boolean;
  /** Switched on in Admin AND able to reach a model. */
  ready: boolean;
  enabled: boolean;
  provider: string;
  model: string;
  /**
   * Whether each keyed provider has a key saved in Admin. The key itself
   * never leaves the service.
   */
  keys: Record<string, boolean>;
}

export interface AssistantMessage {
  id: number;
  role: 'human' | 'ai' | 'system';
  content: string;
  /** What the agent did to answer, kept as the audit trail. */
  tool_calls: { name: string; args: unknown; result: string }[] | null;
  created_at: string;
}

/**
 * One answer an operator can pick, rather than type.
 *
 * `answer` is what gets sent back when it differs from what is shown. The
 * only thing that uses it today is "which of these five places did you
 * mean?", where the assistant resolves the NUMBER against the list it
 * remembers and the name is what makes the transcript readable.
 */
export interface AssistantOption {
  label: string;
  description: string | null;
  answer?: string | null;
}

/** One question, with what it is asking and what may answer it. */
export interface AssistantQuestion {
  /** Two or three words, for the label on the form. */
  header: string;
  question: string;
  options: AssistantOption[];
  /** Whether more than one answer can hold at once. */
  multiSelect: boolean;
  /**
   * What kind of thing answers this, which decides the control drawn for it.
   *
   * A question is not always a list of guesses: "what time do you set off" is
   * a time, "how long have you got" is a number of hours, and "where does it
   * start" is somewhere on the map with ten thousand candidates. Offering any
   * of those as four buttons is offering four guesses, so the control follows
   * the question instead.
   *
   * Unknown values fall back to a plain choice, which is the one that works
   * with no help from anything.
   */
  answerType?: 'choice' | 'place' | 'time' | 'duration' | 'number' | 'text';
  /**
   * Superseded by `answerType: 'place'`, and still sent beside it.
   *
   * Where a round starts and where it ends cannot be offered as a shortlist —
   * there are tens of thousands of candidates — and a typed name has to be
   * guessed at afterwards. Searching picks one exactly, and the id travels
   * back with it. Options may still sit beside the box for the answers that
   * are not places: "Return to the start point" is the whole reason the
   * destination question has both.
   */
  search?: boolean;
}

export interface AssistantSession {
  id: string;
  city_id: string | null;
  title: string | null;
  draft: Partial<PlanRequest>;
  plan: RoutePlan | null;
  trip_draft?: unknown | null;
  trip?: unknown | null;
  updated_at: string;
}

export interface AssistantReply {
  reply: string;
  toolCalls: { name: string; args: unknown; result: string }[];
  /** The trip as it now stands. The page adopts this. */
  draft: Partial<PlanRequest>;
  plan: RoutePlan | null;
  /**
   * Several days, when the turn planned some.
   *
   * A different planner and a different shape, so it is returned separately
   * and the page switches screens rather than trying to draw a week on the
   * one-day map.
   */
  tripRequest: unknown | null;
  trip: unknown | null;
  changed: boolean;
  /**
   * What the assistant is waiting to be told, asked one at a time.
   *
   * Empty on the turns that simply answered. When it is not, nothing was
   * planned — the assistant stopped rather than guessing at the answer.
   */
  questions: AssistantQuestion[];
}

/**
 * The assistant's plan, in the shape the rest of the app speaks.
 *
 * The assistant reaches the planner over REST and hands back what it got,
 * verbatim. The app's own plans come through Connect and are mapped on the way
 * in — and the one thing that mapping does is rename a stop's `id` to `poiId`
 * (see `mapRouteStop` in api/client.ts). So a plan built by the assistant
 * arrived with every field the itinerary needs except that one.
 *
 * What it cost was small and invisible: the itinerary draws a stop's name as a
 * link to its details only when the stop carries a poiId, so every stop in an
 * assistant-built round was plain text. Same route, same map, no way to open
 * the place — and nothing to suggest why, since a round planned from the form
 * beside it behaved perfectly.
 *
 * Done here rather than in the service, because this is the boundary: the
 * service speaks the planner's REST shape and everything past this line speaks
 * the app's.
 */
function withStopIds<T>(value: T): T {
  if (Array.isArray(value)) return value.map(withStopIds) as unknown as T;
  if (!value || typeof value !== 'object') return value;

  const object = value as Record<string, unknown>;
  const next: Record<string, unknown> = {};
  for (const [key, held] of Object.entries(object)) {
    next[key] = withStopIds(held);
  }
  /*
   * Only where it is missing. A shape that already speaks poiId — anything
   * that has been through the Connect mapping — is left exactly as it is.
   */
  if (typeof object.id === 'string' && object.poiId === undefined && 'kind' in object) {
    next.poiId = object.id || null;
  }

  return next as unknown as T;
}

class AssistantError extends Error {}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${aiBaseUrl}/ai${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      // The same opaque browser key that scopes saved trips, so a
      // conversation belongs to the same traveller their saved routes do.
      'x-travplaces-client': clientKey(),
      ...(init.headers ?? {}),
    },
  });
  const text = await response.text();
  const payload = text ? JSON.parse(text) : {};
  if (!response.ok) {
    // FastAPI puts the message in `detail`; it is written to be read.
    throw new AssistantError(payload.detail ?? `The assistant failed (${response.status})`);
  }
  return payload as T;
}

export interface AssistantModel {
  id: string;
  label: string;
  /** Whether it can call tools; null when the provider does not say. */
  tools: boolean | null;
}

/** The outcome of one real request to the configured model. */
export interface AssistantCheck {
  ok: boolean;
  provider: string;
  model: string;
  error: string | null;
  ms: number;
}

/**
 * What the assistant IS, as opposed to what it costs.
 *
 * Read from the running service rather than described beside it: the prompt
 * is the one the model is sent, the workflow is built from the graph's own
 * node names, and the tools are the ones a turn binds. A tool added to the
 * assistant therefore appears in Admin without anybody editing Admin.
 */
export interface AssistantAgent {
  systemPrompt: string;
  settings: {
    enabled: boolean;
    provider: string;
    model: string;
    temperature: number;
    maxIterations: number;
    /** Only present when Ollama is the provider in use. */
    ollamaBaseUrl?: string;
  };
  workflow: {
    engine: string;
    entry: string;
    /** LangGraph's own drawing of the compiled graph, as Mermaid. */
    mermaid: string;
    nodes: { name: string; does: string }[];
    edges: { from: string; to: string; when: string }[];
    /**
     * How a turn puts a question to the operator, when it has one.
     *
     * Read from the service rather than written here, so the page describing
     * the assistant cannot describe an asking mechanism it no longer has.
     * Optional: an older service does not report it.
     */
    asks?: { tool: string; how: string; while_waiting: string };
  };
  tools: { name: string; does: string }[];
}

export const assistant = {
  health: () => call<AssistantHealth>('/health'),

  /** The prompt, the settings, the graph and the tools it can reach. */
  agent: () => call<AssistantAgent>('/agent'),

  /** Sends the configured model one tiny prompt and reports how it went. */
  check: () => call<AssistantCheck>('/check', { method: 'POST' }),

  /** What the provider will serve, asked with the key the assistant would use. */
  models: (provider: string) =>
    call<{ provider: string; models: AssistantModel[]; error: string | null }>(
      `/models?provider=${encodeURIComponent(provider)}`,
    ),

  sessions: () => call<{ sessions: AssistantSession[] }>('/sessions'),

  /**
   * A conversation as it stands, including a question it is still waiting on.
   *
   * The questions come back here as well as with a reply so that closing the
   * panel, or reloading, returns to the form rather than to an answer that
   * reads as though it asked nothing.
   */
  open: (id: string) =>
    call<{
      session: AssistantSession;
      messages: AssistantMessage[];
      questions?: AssistantQuestion[];
    }>(`/sessions/${id}`).then(withStopIds),

  start: (cityId: string | null) =>
    call<{ session: AssistantSession; messages: AssistantMessage[] }>('/sessions', {
      method: 'POST',
      body: JSON.stringify({ city_id: cityId }),
    }),

  remove: (id: string) => call<{ deleted: boolean }>(`/sessions/${id}`, { method: 'DELETE' }),

  send: (id: string, message: string, draft?: Partial<PlanRequest>) =>
    call<AssistantReply>(`/sessions/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify({ message, ...(draft ? { draft } : {}) }),
    }).then(withStopIds),

  /**
   * Tells the assistant what the traveller just did on the page.
   *
   * Sent when a stop is dragged, a pin dropped or an endpoint moved, so that
   * "avoid that one" a minute later is answered against what is on screen
   * rather than against whatever the assistant last built itself.
   */
  pushState: (
    id: string,
    state: { draft?: Partial<PlanRequest>; plan?: RoutePlan | null; note?: string },
  ) =>
    call<{ ok: boolean }>(`/sessions/${id}/state`, {
      method: 'POST',
      body: JSON.stringify(state),
    }),
};
